import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import {
  Wrench, MapPin, Camera, FileText, CheckCircle2, Clock, LogOut, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import RevaLogo from '@/components/RevaLogo';

// ─────────────────────────────────────────────────────────────────────────────
// Vendor Portal — mobile-first work-order experience for Saudi contractors.
// No sidebar, no chrome. Designed for hands on a phone in a service van.
// Route: /vendor-portal
// ─────────────────────────────────────────────────────────────────────────────
export default function VendorPortal() {
  const { user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getMaintenance();
        // Vendor only sees their own assignments — backend would filter by vendor_id on a real run
        setTasks(data || []);
      } catch (e) {
        toast.error('Failed to load work orders');
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF8EE]" data-testid="vendor-portal-page">
      {/* Top bar — mobile-first */}
      <header className="sticky top-0 z-30 bg-[#7C3F00] text-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RevaLogo color="white" />
            <span className="text-[10.5px] reva-mono uppercase tracking-[0.18em] opacity-80">Vendor</span>
          </div>
          <button onClick={() => { logout(); window.location.href = '/login/vendor'; }} className="inline-flex items-center gap-1 text-[12px] opacity-90 hover:opacity-100" data-testid="vendor-logout">
            <LogOut className="h-3.5 w-3.5" /> Exit
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-5 pb-24">
        <div className="rounded-2xl bg-[#D97706] text-white p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] reva-mono opacity-80">Field operator</div>
          <div className="mt-1 text-[18px] font-semibold">Hello, {user?.full_name || 'Vendor'}</div>
          <p className="mt-1 text-[12.5px] opacity-90">{tasks.filter(t => t.status === 'open').length} open · {tasks.filter(t => t.status === 'in_progress').length} in progress</p>
        </div>

        <div className="mt-4 text-[11px] reva-mono uppercase tracking-[0.18em] text-[#7C3F00]">My work orders</div>

        <div className="mt-3 space-y-3" data-testid="vendor-tasks-list">
          {loading && <div className="text-center py-12 text-[#7C3F00]/60 text-[13px]">Loading…</div>}
          {!loading && tasks.length === 0 && (
            <div className="text-center py-12 rounded-2xl border-2 border-dashed border-[#D9770633] text-[#7C3F00]">
              <Wrench className="h-8 w-8 mx-auto mb-2" strokeWidth={1.4} />
              <div className="text-[13px] font-medium">No assignments yet</div>
              <div className="text-[11.5px] opacity-70 mt-0.5">Your property manager will push work orders here.</div>
            </div>
          )}
          {tasks.map(t => (
            <article key={t.id} className="rounded-2xl bg-white border border-[#0E132014] p-4 shadow-sm" data-testid={`vendor-task-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium reva-mono uppercase tracking-[0.12em] ${
                      t.priority === 'high' ? 'bg-red-100 text-red-800' :
                      t.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
                      'bg-emerald-50 text-emerald-700'}`}>{t.priority || 'normal'}</span>
                    <span className="text-[10.5px] reva-mono uppercase tracking-[0.12em] text-[#71777E]">{t.status || 'open'}</span>
                  </div>
                  <h3 className="mt-2 text-[14px] font-semibold text-[#0E1320]">{t.title || 'Maintenance request'}</h3>
                  <p className="mt-1 text-[12.5px] text-[#4A5263] line-clamp-2">{t.description}</p>
                  {t.location && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-[#71777E]">
                      <MapPin className="h-3 w-3" /> {t.location}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#FFF8EE] border border-[#D9770644] text-[11.5px] font-medium text-[#7C3F00] hover:bg-[#FCEFD7]" data-testid={`vendor-photo-before-${t.id}`}>
                  <Camera className="h-3 w-3" /> Before photo
                </button>
                <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#FFF8EE] border border-[#D9770644] text-[11.5px] font-medium text-[#7C3F00] hover:bg-[#FCEFD7]" data-testid={`vendor-photo-after-${t.id}`}>
                  <Camera className="h-3 w-3" /> After photo
                </button>
                <button className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#FFF8EE] border border-[#D9770644] text-[11.5px] font-medium text-[#7C3F00] hover:bg-[#FCEFD7]" data-testid={`vendor-invoice-${t.id}`}>
                  <FileText className="h-3 w-3" /> Upload invoice
                </button>
                <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D97706] text-white text-[11.5px] font-semibold hover:bg-[#B45309]" data-testid={`vendor-mark-done-${t.id}`}>
                  <CheckCircle2 className="h-3 w-3" /> Mark done
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* SLA banner */}
        <div className="mt-6 rounded-2xl bg-white border border-[#D9770622] p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 mt-0.5 text-[#7C3F00]" strokeWidth={1.7} />
            <div>
              <div className="text-[12.5px] font-semibold text-[#7C3F00]">SLA reminder</div>
              <p className="mt-0.5 text-[11.5px] text-[#4A5263]">High-priority work must be acknowledged within 30 minutes and on-site within 2 hours per your vendor contract.</p>
            </div>
          </div>
        </div>

        {/* Mobile-first footer */}
        <div className="mt-6 text-center text-[10.5px] text-[#7C3F00]/70 reva-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 justify-center w-full">
          <Smartphone className="h-3 w-3" /> Mobile-first by design
        </div>
      </main>
    </div>
  );
}
